import React, { useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'motion/react';
import { Phone, X, BellRing } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { UNIQUE_CODES } from '../lib/countryCodes';
import { formatPhoneNumber } from '../lib/phone';

interface PhoneNumberPromptProps {
  user: User;
}

// Only nag once per browser session if they dismiss it — they'll still see
// it again next time they sign in, until they actually add a number.
const DISMISS_KEY = 'yhuu_phone_prompt_dismissed';

// Shown to a signed-in user who hasn't given us a phone number yet. We use
// it to alert them (by SMS/WhatsApp, sent manually from the admin
// dashboard) the moment one of their sessions gets answered. Saving closes
// the prompt immediately — no extra confirmation step.
export default function PhoneNumberPrompt({ user }: PhoneNumberPromptProps) {
  const [checked, setChecked] = useState(false);
  const [visible, setVisible] = useState(false);
  const [countryIso, setCountryIso] = useState('GH');
  const [rawNumber, setRawNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (sessionStorage.getItem(DISMISS_KEY)) { setChecked(true); return; }
      const { data } = await supabase
        .from('profiles')
        .select('phone_number')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (!data?.phone_number) setVisible(true);
      setChecked(true);
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  const selectedCountry = UNIQUE_CODES.find(c => c.iso === countryIso) ?? UNIQUE_CODES[0];

  const handleSave = async () => {
    // Real phone-numbering-plan validation instead of naive "code + digits"
    // concatenation — catches a re-typed country code or a leading trunk 0
    // instead of silently saving a number that can never be reached.
    const { e164, valid } = formatPhoneNumber(rawNumber, countryIso);
    if (!valid || !e164) {
      setError(`That doesn't look like a valid ${selectedCountry.name} number — just type the local number, the ${selectedCountry.code} is already added for you.`);
      return;
    }
    setError('');
    setSaving(true);
    const phone_number = e164;
    try {
      const { error: dbError } = await supabase
        .from('profiles')
        .upsert({ id: user.id, phone_number }, { onConflict: 'id' });
      if (dbError) throw dbError;
      setVisible(false); // vanishes the instant it's saved
    } catch (err) {
      console.error('Failed to save phone number:', err);
      setError("Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  if (!checked || !visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className="absolute inset-0"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={handleDismiss}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        />

        <motion.div
          className="relative w-full max-w-sm rounded-2xl border border-brand-border overflow-hidden"
          style={{ background: 'var(--color-brand-card)' }}
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 340, damping: 30 }}
        >
          <div
            className="flex items-center justify-between px-5 py-4 border-b border-brand-border"
            style={{ background: 'var(--color-brand-bg)' }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.18em] flex items-center gap-2" style={{ color: 'var(--color-brand-accent)' }}>
              <BellRing className="w-3.5 h-3.5" />
              Get notified
            </span>
            <button
              onClick={handleDismiss}
              className="w-7 h-7 rounded-full flex items-center justify-center border border-brand-border transition-colors hover:border-brand-accent text-zinc-500 hover:text-brand-accent"
              style={{ background: 'var(--color-brand-card)' }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="px-6 pt-6 pb-6">
            <p className="text-sm text-zinc-500 mb-5 leading-relaxed">
              Add your active phone number and we'll alert you the moment someone answers your Yhuu — by SMS or WhatsApp, the second their reply lands.
            </p>

            <div className="flex gap-2">
              <select
                value={countryIso}
                onChange={e => setCountryIso(e.target.value)}
                className="rounded-xl text-sm pl-2 pr-1 border border-brand-border focus:outline-none focus:border-brand-accent"
                style={{ background: 'var(--color-brand-bg)', color: 'var(--color-brand-text)', maxWidth: 108 }}
              >
                {UNIQUE_CODES.map(c => (
                  <option key={`${c.iso}-${c.code}`} value={c.iso}>{c.flag} {c.code}</option>
                ))}
              </select>
              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={rawNumber}
                  onChange={e => setRawNumber(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                  className="w-full pl-10 pr-4 py-3 rounded-xl text-sm focus:outline-none focus:border-brand-accent border border-brand-border"
                  style={{ background: 'var(--color-brand-bg)', color: 'var(--color-brand-text)' }}
                />
              </div>
            </div>

            {error && <p className="text-rose-400 text-sm mt-2">{error}</p>}

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full py-3 bg-brand-accent text-white rounded-xl font-bold hover:opacity-90 transition-all disabled:opacity-50 cursor-pointer mt-4"
            >
              {saving ? 'Saving...' : 'Notify Me'}
            </button>

            <button
              onClick={handleDismiss}
              className="text-zinc-500 text-xs text-center hover:text-zinc-300 transition-colors mt-3 w-full"
            >
              Maybe later
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
